import React, { useState, useCallback } from "react";

import { Upload, Modal, Tooltip, Spin } from "antd";
import ImageCrop from "../ImageCrop";
import { PlusCircleFilled } from "@ant-design/icons";
import imageCompression from "browser-image-compression";
import heic2any from "heic2any";
import logger from "../../../utils/logger";

const uploadButton = (
  <div>
    <PlusCircleFilled />
    <div className="ant-upload-text">Upload</div>
  </div>
);

const FUpload = (props) => {
  const {
    showCropOptions = true,
    isAvatar,
    display,
    style,
    label,
    required,
    tooltip,
    onRemove,
    fileList,
    viewMode,
    onChange,
    accept,
    action,
    onLoad,
    aspectRatio,
    membershipCard,
  } = props;

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [isCompressing, setIsCompressing] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [showCrop, setShowCrop] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);
  const [baseFile, setBaseFile] = useState(null);
  const [resolveUpload, setResolveUpload] = useState(null);
  const [rejectUpload, setRejectUpload] = useState(null);

  const getBase64 = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }, []);

  // New method to convert HEIC to JPEG
  const convertHeicToJpeg = useCallback(async (file) => {
    try {
      // Check if file is HEIC
      const isHeic =
        file.type === "image/heic" ||
        file.type === "image/heif" ||
        file.name.toLowerCase().endsWith(".heic") ||
        file.name.toLowerCase().endsWith(".heif");

      if (!isHeic) {
        return file;
      }

      setIsConverting(true);
      logger.info("[FUpload] Converting HEIC/HEIF to JPEG", {
        name: file.name,
        type: file.type,
        size: file.size,
      });

      // Convert HEIC to JPEG
      const convertedBlob = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.9,
      });

      // Create new File object from the converted blob
      const convertedFile = new File(
        [convertedBlob],
        file.name.replace(/\.(heic|heif)$/i, ".jpg"),
        { type: "image/jpeg", lastModified: Date.now() },
      );

      logger.info("[FUpload] HEIC conversion done", {
        outputType: convertedFile.type,
        outputSize: convertedFile.size,
      });

      setIsConverting(false);
      return convertedFile;
    } catch (error) {
      console.error("Error converting HEIC to JPEG:", error);
      setIsConverting(false);
      throw error;
    }
  }, []);

  const compressImageIfNeeded = useCallback(
    async (file, maxWidth = 9000, maxHeight = 9000) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = async () => {
          URL.revokeObjectURL(url);

          try {
            // If image dimensions exceed the limit, compress it
            if (img.width > maxWidth || img.height > maxHeight) {
              // Set loading state
              setIsCompressing(true);

              const compressionOptions = {
                maxWidthOrHeight: Math.min(maxWidth, maxHeight),
                useWebWorker: true,
                fileType: file.type,
                initialQuality: 0.8,
              };

              const compressedFile = await imageCompression(
                file,
                compressionOptions,
              );

              // Clear loading state
              setIsCompressing(false);
              resolve(compressedFile);
            } else {
              // Image is within limits, return as-is
              resolve(file);
            }
          } catch (error) {
            console.error("Error compressing image:", error);
            // Clear loading state on error
            setIsCompressing(false);
            // If compression fails, return original file
            resolve(file);
          }
        };

        img.onerror = () => {
          URL.revokeObjectURL(url);
          setIsCompressing(false);
          reject(new Error("Failed to load image for compression check."));
        };

        img.src = url;
      });
    },
    [],
  );

  const handlePreview = useCallback(
    async (file) => {
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj);
      }

      setPreviewImage(file.url || file.preview);
      setPreviewVisible(true);
    },
    [getBase64],
  );

  const handleCancel = useCallback(() => {
    setPreviewVisible(false);

    // if (rejectUpload) {
    //   rejectUpload("Upload canceled by user");
    // }

    // setShowCrop(false);
    // setOriginalFile(null);
    // setBaseFile(null);
    // setResolveUpload(null);
    // setRejectUpload(null);
  }, []);

  const handleOk = useCallback(
    (croppedFile) => {
      if (resolveUpload) {
        resolveUpload(croppedFile);
      }

      setShowCrop(false);
      setOriginalFile(null);
      setBaseFile(null);
      setResolveUpload(null);
      setRejectUpload(null);
    },
    [resolveUpload],
  );

  const handleCropComplete = useCallback(
    (imageFile) => {
      logger.info("[FUpload] handleCropComplete called", {
        type: imageFile?.type,
        size: imageFile?.size,
        isFile: imageFile instanceof File,
        isBlob: imageFile instanceof Blob,
      });

      // Validate the cropped output is actually an image type.
      // Bug report mentions type sometimes being 'document'.
      let validatedFile = imageFile;
      if (imageFile && !imageFile.type?.startsWith("image/")) {
        logger.warn(
          "[FUpload] Crop output has non-image MIME type, forcing image/jpeg",
          { originalType: imageFile.type },
        );
        validatedFile = new File([imageFile], imageFile.name || "image.jpeg", {
          type: "image/jpeg",
          lastModified: Date.now(),
        });
      }

      setShowCrop(false);
      if (onLoad) {
        getBase64(validatedFile).then((baseFile) => {
          onLoad(baseFile);
        });
      }
      onChange({
        fileList: [{ ...originalFile, originFileObj: validatedFile }],
      });
    },
    [onLoad, onChange, originalFile, getBase64],
  );

  const handleCancelImageCrop = useCallback(() => {
    setShowCrop(false);
    setOriginalFile("");
  }, []);

  const beforeUpload = useCallback(
    (file) => {
      logger.info("[FUpload] beforeUpload started", {
        name: file.name,
        type: file.type,
        size: file.size,
      });

      const processImage = async () => {
        try {
          // First convert HEIC to JPEG if needed
          const convertedFile = await convertHeicToJpeg(file);

          // Then compress the image if needed
          const processedFile = await compressImageIfNeeded(convertedFile);

          // Then proceed with base64 conversion
          const baseFile = await getBase64(processedFile);

          logger.info("[FUpload] Processing pipeline complete", {
            finalType: processedFile.type,
            finalSize: processedFile.size,
          });

          return { processedFile, baseFile };
        } catch (error) {
          console.error("Error processing image:", error);
          throw error;
        }
      };

      return new Promise((resolve, reject) => {
        processImage()
          .then(({ processedFile, baseFile }) => {
            if (showCropOptions) {
              setShowCrop(true);
              setOriginalFile(processedFile);
              setBaseFile(baseFile);
              setResolveUpload(() => resolve);
              setRejectUpload(() => reject);
              logger.info("[FUpload] Crop modal opened, waiting for user");
            } else {
              if (action) {
                action(processedFile);
              }
              if (onLoad) {
                onLoad(baseFile);
              }
              resolve(processedFile);
            }
          })
          .catch(reject);
      });
    },
    [
      convertHeicToJpeg,
      compressImageIfNeeded,
      getBase64,
      showCropOptions,
      action,
      onLoad,
    ],
  );

  const customRequest = useCallback(
    ({ file, onSuccess }) => {
      if (action) {
        action(file);
      }
      setTimeout(() => {
        onSuccess("ok");
      }, 0);
    },
    [action],
  );

  const isProcessing = isCompressing || isConverting;

  // Get loading message
  const getLoadingMessage = () => {
    if (isConverting) return "Converting HEIC to JPEG...";
    if (isCompressing) return "Compressing image...";
    return "Processing...";
  };

  return (
    <div
      className={`form-group ${display !== "col" ? "my-row" : "col"}`}
      style={{ ...style, minHeight: "100px", height: "100px" }}
    >
      <div className="col-4" style={!label ? { height: 0 } : {}}>
        <label>
          {" "}
          {label}
          {required && <span style={{ color: "red" }}>*</span>}
        </label>
        {tooltip && (
          <Tooltip
            placement="topLeft"
            title={() => (
              <div
                style={{
                  height: "fit-content",
                  width: "400px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {tooltip}
              </div>
            )}
          >
            <span className="border-0">
              &nbsp;<i className="fa fa-question-circle-o"></i>
            </span>
          </Tooltip>
        )}
      </div>
      <div className="clearfix">
        <Spin
          spinning={isProcessing}
          tip={getLoadingMessage()}
          size="large"
          style={{
            display: "block",
            minHeight: isProcessing ? "120px" : "auto",
          }}
        >
          <Upload
            onRemove={onRemove}
            listType="picture-card"
            fileList={fileList}
            onPreview={handlePreview}
            onChange={viewMode ? undefined : onChange}
            width="500"
            accept={accept || ".jpg,.jpeg,.png,.heic,.heif"}
            beforeUpload={beforeUpload}
            className={`${aspectRatio ? aspectRatio : "si9"}`}
            openFileDialogOnClick={!viewMode}
            customRequest={customRequest}
          >
            {fileList.length >= 1 ? null : uploadButton}
          </Upload>
        </Spin>

        <Modal
          open={previewVisible}
          footer={null}
          onCancel={handleCancel}
          onOk={handleOk}
        >
          <div style={{ padding: "30px 0px" }}>
            <img alt="example" style={{ width: "100%" }} src={previewImage} />
          </div>
        </Modal>

        {showCrop && (
          <ImageCrop
            className="ReactCrop__crop-selection"
            membershipCard={membershipCard}
            isAvatar={isAvatar}
            src={baseFile}
            handleCancelImageCrop={handleCancelImageCrop}
            handleCropComplete={handleCropComplete}
            aspectRatio={aspectRatio}
          />
        )}
      </div>
    </div>
  );
};

FUpload.defaultProps = {
  showCropOptions: true,
};

export default FUpload;
